/**
 * One order for everything a committed descent reads.
 *
 * A landing wants three things off the wire and wants them in a fixed order:
 * the claim the walkaround is built from, the tile ring the camera reaches
 * first, and then the rest of the pyramid. Before this they were three
 * mechanisms - the claim's own clamp on the tile queue, a warm loop that
 * fetched tiles beside it, and the tree's internal ordering - and each could
 * only be reasoned about against the other two by reading all three.
 *
 * The tile stream is the last rung and is HELD rather than carried: the tree
 * owns its own traversal order and its own cancellation, so what this queue
 * does to it is clamp its in-flight cap and its depth while a nearer rung is
 * still out. Every other descent read goes through `run` and waits for the
 * rungs in front of it.
 */

/** The rungs, nearest the player first. */
export const DESCENT_RUNG = {
  /** The claim: its height field, its orthoimage, and the survey scored on them. */
  CLAIM: 0,
  /** The tile ring around the committed site, read ahead of the stream. */
  RING: 1,
} as const;

export interface DescentRungConfig {
  readonly name: string;
  /** Reads this rung runs at once. */
  readonly maxInFlight: number;
  /**
   * Longest one entry may keep the rungs behind it waiting, however it is
   * going. A read that never settles must not leave the ground the player is
   * looking at frozen, so the order is a preference and not a lock.
   */
  readonly ceilingMs: number;
  /** Whether an outstanding entry clamps the tile stream at all. */
  readonly holdsStream: boolean;
  /**
   * Deepest level the stream may reach while this rung clamps it. Null admits
   * every level, so the cap alone does the holding.
   */
  readonly holdMaxLevel: number | null;
}

export interface DescentQueueConfig {
  /** Rungs in priority order; the index into this array IS the rung number. */
  readonly rungs: readonly DescentRungConfig[];
  /** Applied to the tile stream whenever the clamp changes. */
  readonly onHold: (holding: boolean, maxLevel: number | null) => void;
}

export interface DescentRungStats {
  readonly name: string;
  /** Entries admitted and not yet released. */
  readonly out: number;
  /** Reads held back by a nearer rung or by this rung's own cap. */
  readonly waiting: number;
  /** Entries this rung has admitted over the life of the queue. */
  readonly admitted: number;
}

export interface DescentQueueStats {
  readonly holding: boolean;
  readonly holdMaxLevel: number | null;
  readonly rungs: readonly DescentRungStats[];
}

export interface DescentQueue {
  /**
   * Take a slot on `rung` without waiting for one, for a read the caller
   * already has in hand. The release is idempotent and is also fired by the
   * rung's own ceiling.
   */
  enter(rung: number): () => void;
  /** Run `job` on `rung` once every nearer rung is clear and a slot is free. */
  run<T>(rung: number, job: () => Promise<T>): Promise<T>;
  /** Re-issue the clamp onto a stream opened after it went on. */
  reapplyHold(): void;
  /**
   * Release every entry out right now, leaving the queue open. What a pane
   * that closes mid-read owes the stream it is leaving behind.
   */
  drain(): void;
  stats(): DescentQueueStats;
  dispose(): void;
}

interface RungState {
  readonly cfg: DescentRungConfig;
  out: number;
  waiting: number;
  admitted: number;
}

export function createDescentQueue(config: DescentQueueConfig): DescentQueue {
  const rungs: RungState[] = config.rungs.map(
    (cfg) => ({ cfg, out: 0, waiting: 0, admitted: 0 }),
  );
  const waiters = new Set<() => void>();
  const outstanding = new Set<() => void>();
  let holding = false;
  let holdMaxLevel: number | null = null;
  let disposed = false;

  const at = (rung: number): RungState => {
    const r = rungs[rung];
    if (!r) throw new Error(`the descent queue has no rung ${rung}`);
    return r;
  };

  function syncHold(force: boolean): void {
    let next = false;
    let level: number | null = null;
    let everyLevel = false;
    for (const r of rungs) {
      if (r.out === 0 || !r.cfg.holdsStream) continue;
      next = true;
      if (r.cfg.holdMaxLevel === null) everyLevel = true;
      else level = level === null ? r.cfg.holdMaxLevel : Math.min(level, r.cfg.holdMaxLevel);
    }
    const nextLevel = !next || everyLevel ? null : level;
    if (!force && next === holding && nextLevel === holdMaxLevel) return;
    holding = next;
    holdMaxLevel = nextLevel;
    config.onHold(holding, holdMaxLevel);
  }

  function wake(): void {
    for (const w of [...waiters]) w();
  }

  function enter(rung: number): () => void {
    if (disposed) return () => { /* a closed queue holds nothing */ };
    const r = at(rung);
    r.out += 1;
    r.admitted += 1;
    syncHold(false);
    let done = false;
    const release = (): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      outstanding.delete(release);
      r.out -= 1;
      syncHold(false);
      wake();
    };
    const timer = setTimeout(release, r.cfg.ceilingMs);
    outstanding.add(release);
    return release;
  }

  /** Nothing nearer is out or waiting, and this rung has a slot. */
  function admits(rung: number): boolean {
    for (let i = 0; i < rung; i++) {
      const nearer = rungs[i];
      if (nearer && (nearer.out > 0 || nearer.waiting > 0)) return false;
    }
    const r = at(rung);
    return r.out < r.cfg.maxInFlight;
  }

  async function run<T>(rung: number, job: () => Promise<T>): Promise<T> {
    const r = at(rung);
    while (!disposed && !admits(rung)) {
      r.waiting += 1;
      try {
        await new Promise<void>((resolve) => {
          const w = (): void => { waiters.delete(w); resolve(); };
          waiters.add(w);
        });
      } finally {
        r.waiting -= 1;
      }
    }
    if (disposed) throw new Error('the descent queue closed before this read started');
    const release = enter(rung);
    try {
      return await job();
    } finally {
      release();
    }
  }

  return {
    enter,
    run,
    reapplyHold(): void {
      syncHold(true);
    },
    drain(): void {
      for (const release of [...outstanding]) release();
    },
    stats(): DescentQueueStats {
      return {
        holding,
        holdMaxLevel,
        rungs: rungs.map((r) => ({
          name: r.cfg.name, out: r.out, waiting: r.waiting, admitted: r.admitted,
        })),
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const release of [...outstanding]) release();
      outstanding.clear();
      wake();
      waiters.clear();
      if (holding) {
        holding = false;
        holdMaxLevel = null;
        config.onHold(false, null);
      }
    },
  };
}
